// Risk-signal fields on a screening hit's OpenSearch `_source`, pulled out of
// the record so the record drawers can show them in their own "Risk Signals"
// box above Entity/Product Details. Shared by every drawer with a Risk Signals
// box: the screening tools, BITE DB, Monitor, ScreeningComposer and the
// Export Hub's EntityRecordDrawer. "What This Hit Means" (the dataset's plain-
// language explanation of the hit) is shown in the same box, below the signals.
//
// The keys come straight from the indexed datasets rather than a fixed schema,
// so matching ignores case, spacing and underscores ("Primary Risk Signal",
// "primary_risk_signal", "Secondary Risk Signals", ...).

const normalizeKey = (key: string): string =>
  key.toLowerCase().replace(/[\s_-]+/g, '');

const RISK_SIGNAL_FIELDS = [
  {
    label: 'Primary Risk Signal',
    keys: ['primaryrisksignal', 'primaryrisksignals', 'primarysignal'],
  },
  {
    label: 'Secondary Signals',
    keys: [
      'secondarysignals',
      'secondarysignal',
      'secondaryrisksignals',
      'secondaryrisksignal',
    ],
  },
  {
    label: 'What This Hit Means',
    keys: ['whatthishitmeans'],
  },
];

const isPresent = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.some((v) => isPresent(v));
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return String(value).trim() !== '';
};

export interface RiskSignalRow {
  label: string;
  value: any;
}

export interface SplitRiskSignals {
  // Non-empty risk-signal fields, in display order. Empty → render no box.
  signals: RiskSignalRow[];
  // The source minus every risk-signal key (empty ones included), for the
  // details box, so the same field never shows twice.
  rest: Record<string, any>;
}

// Deletes every key in `keys` from `rest` and returns the first non-empty value.
const takeField = (rest: Record<string, any>, keys: string[]): any => {
  const matched = Object.keys(rest).filter((k) =>
    keys.includes(normalizeKey(k)),
  );
  const value = matched.map((k) => rest[k]).find(isPresent);
  matched.forEach((k) => delete rest[k]);
  return value;
};

export const splitRiskSignals = (
  source: Record<string, any> | null | undefined,
): SplitRiskSignals => {
  const rest: Record<string, any> = { ...(source || {}) };
  const signals: RiskSignalRow[] = [];

  RISK_SIGNAL_FIELDS.forEach(({ label, keys }) => {
    const value = takeField(rest, keys);
    if (value !== undefined) signals.push({ label: label, value: value });
  });

  return { signals: signals, rest: rest };
};

const HIT_MEANING_KEYS = ['whatthishitmeans'];

export interface SplitHitMeaning {
  // Null when the record has no non-empty value. Null → render no box.
  meaning: any;
  rest: Record<string, any>;
}

export const splitHitMeaning = (
  source: Record<string, any> | null | undefined,
): SplitHitMeaning => {
  const rest: Record<string, any> = { ...(source || {}) };
  const meaning = takeField(rest, HIT_MEANING_KEYS);
  return { meaning: meaning === undefined ? null : meaning, rest: rest };
};
